import { useEffect } from "react";
import { useCanonical } from "./useCanonical";

type SeoParams = {
  title: string;
  description: string;
  canonical?: string;
  image?: string;
  type?: "website" | "article";
  noindex?: boolean;

  locale?: string; // ✅ novo (ex: "pt_BR", "en_US")
};


function upsertMeta(nameOrProp: { name?: string; property?: string }, content: string) {
  const selector = nameOrProp.name
    ? `meta[name="${nameOrProp.name}"]`
    : `meta[property="${nameOrProp.property}"]`;

  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement("meta");
    if (nameOrProp.name) el.setAttribute("name", nameOrProp.name);
    if (nameOrProp.property) el.setAttribute("property", nameOrProp.property);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

export function useSeo({
  title,
  description,
  canonical,
  image,
  type = "website",
  noindex = false,
  locale = "pt_BR",
}: SeoParams) {
  useCanonical({ canonical, stripQueryAndHash: true });

  useEffect(() => {
    document.title = title;

    upsertMeta({ name: "description" }, description);
    upsertMeta({ name: "robots" }, noindex ? "noindex,nofollow" : "index,follow");

    // Open Graph
    upsertMeta({ property: "og:title" }, title);
    upsertMeta({ property: "og:description" }, description);
    upsertMeta({ property: "og:type" }, type);
    upsertMeta({ property: "og:locale" }, locale);

    const url = canonical
      ? canonical
      : (() => {
          const u = new URL(window.location.href);
          u.search = "";
          u.hash = "";
          return u.toString();
        })();

    upsertMeta({ property: "og:url" }, url);

    if (image) {
      upsertMeta({ property: "og:image" }, image);
      upsertMeta({ name: "twitter:image" }, image);
    }

    // Twitter
    upsertMeta({ name: "twitter:card" }, "summary_large_image");
    upsertMeta({ name: "twitter:title" }, title);
    upsertMeta({ name: "twitter:description" }, description);
  }, [title, description, canonical, image, type, noindex, locale]);
}
